/**
 * EmailVerificationBanner
 *
 * Displays a persistent top-of-page banner when the user's email is unverified.
 * - Shows countdown of days remaining in the 3-day grace period
 * - Allows user to request a verification code and submit it inline
 * - Disappears once email is verified
 * - After 3 days, the backend will reject API calls with EMAIL_VERIFICATION_REQUIRED
 */

import React, { useState, useEffect, useCallback } from 'react';
import { sendEmailVerificationCode, verifyEmailCode, getAccessToken } from '../../services/cognito';
import styles from './EmailVerificationBanner.module.css';

const API_BASE = process.env.REACT_APP_API_URL || process.env.REACT_APP_LANDLORD_PRO_URL || '';

interface VerificationStatus {
  emailVerified: boolean;
  email: string;
  daysRemaining: number;
  gracePeriodExpired: boolean;
  signupDate: string;
}

const EmailVerificationBanner: React.FC = () => {
  const [status, setStatus] = useState<VerificationStatus | null>(null);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [dismissed, setDismissed] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const token = await getAccessToken();
      const res = await fetch(`${API_BASE}/api/auth/email-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      setStatus(data);
    } catch {
      // Silently fail — banner is non-blocking
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Don't render if verified, dismissed (within grace period only), or no status
  if (!status || status.emailVerified) return null;
  if (dismissed && !status.gracePeriodExpired) return null;

  const handleSendCode = async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      await sendEmailVerificationCode();
      setCodeSent(true);
      setMessage(`Verification code sent to ${status.email}`);
    } catch (err: any) {
      setError(err.message || 'Failed to send code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!code.trim()) {
      setError('Please enter the verification code.');
      return;
    }
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const accessToken = await getAccessToken();

      // Send code to backend ONLY — backend handles Cognito verification + DynamoDB update
      // Do NOT call verifyEmailCode() client-side as it consumes the code before backend can use it
      const res = await fetch(`${API_BASE}/api/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`
        },
        body: JSON.stringify({ code: code.trim(), accessToken })
      });

      const data = await res.json();
      if (data.success) {
        setStatus({ ...status!, emailVerified: true });
        setMessage('Email verified successfully!');
      } else {
        if (data.message?.includes('expired')) {
          setError('Verification code has expired. Please request a new one.');
          setCodeSent(false);
        } else if (data.message?.includes('Invalid')) {
          setError('Invalid code. Please check and try again.');
        } else {
          setError(data.message || 'Verification failed.');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isUrgent = status.daysRemaining <= 1 || status.gracePeriodExpired;

  return (
    <div className={`${styles.banner} ${isUrgent ? styles.urgent : styles.warning}`}>
      <div className={styles.inner}>
        <div className={styles.left}>
          <span className={styles.icon}>{isUrgent ? '🚨' : '📧'}</span>
          <div className={styles.text}>
            {status.gracePeriodExpired ? (
              <strong>Your access has been restricted — please verify your email to continue.</strong>
            ) : (
              <>
                <strong>Please verify your email address.</strong>
                {' '}You have{' '}
                <span className={styles.countdown}>
                  {status.daysRemaining} day{status.daysRemaining !== 1 ? 's' : ''}
                </span>
                {' '}remaining before access is restricted.
              </>
            )}
          </div>
        </div>

        <div className={styles.right}>
          {!codeSent ? (
            <button
              className={styles.sendBtn}
              onClick={handleSendCode}
              disabled={loading}
            >
              {loading ? 'Sending…' : 'Send Verification Code'}
            </button>
          ) : (
            <div className={styles.codeEntry}>
              <input
                type="text"
                placeholder="Enter 6-digit code"
                value={code}
                onChange={e => setCode(e.target.value)}
                maxLength={6}
                className={styles.codeInput}
              />
              <button
                className={styles.verifyBtn}
                onClick={handleVerify}
                disabled={loading}
              >
                {loading ? 'Verifying…' : 'Verify'}
              </button>
              <button
                className={styles.resendLink}
                onClick={handleSendCode}
                disabled={loading}
              >
                Resend
              </button>
            </div>
          )}

          {!status.gracePeriodExpired && (
            <button className={styles.dismissBtn} onClick={() => setDismissed(true)}>
              ✕
            </button>
          )}
        </div>
      </div>

      {(message || error) && (
        <div className={`${styles.feedback} ${error ? styles.feedbackError : styles.feedbackSuccess}`}>
          {error || message}
        </div>
      )}
    </div>
  );
};

export default EmailVerificationBanner;
